import { ArrowLeft, ArrowRight, BookOpen } from 'lucide-react';
import { Link, NavLink, useParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { GuideSteps } from '@/modules/marketing/GuideSteps';
import { GUIDES, guideBySlug, stepAnchor } from '@/modules/marketing/guides';
import { SiteFooter } from '@/modules/marketing/SiteFooter';
import { SiteHeader } from '@/modules/marketing/SiteHeader';
import { NotFoundPage } from '@/modules/system/NotFoundPage';
import { usePageMeta } from '@/lib/seo';

/**
 * One task, start to finish, with a picture of every screen it passes through.
 *
 * ## Laid out as documentation, not as an article
 *
 * The first version was a single centred column, which is the right shape for something read once
 * from the top. A guide is not that: it is opened at step four, from a search result or a support
 * reply, by somebody who already has the dashboard in another tab. That reader needs to see where
 * they are and what else exists without scrolling to find out, so the page carries the two rails a
 * documentation site always has — every guide down the left, this guide's steps down the right —
 * and the content column keeps a comfortable measure between them.
 *
 * The left rail uses the same active treatment as the dashboard sidebar (solid primary, not a
 * tint), because a customer moving between the product and its documentation should not have to
 * re-learn what "you are here" looks like.
 *
 * ## Numbered steps, not prose
 *
 * Somebody reading this is scanning for the step they are on. Paragraphs make that scan fail, which
 * is why each step is a numbered heading, two sentences and a figure, threaded on a rail so the
 * sequence is visible even when a figure is taller than the screen.
 */
export function GuidePage() {
  const { slug } = useParams<{ slug: string }>();
  const guide = guideBySlug(slug ?? '');

  usePageMeta({
    // The searchable form of the title, matching the pre-rendered head; the page itself shows the
    // short one.
    title: guide?.seoTitle ?? 'Guide not found',
    description: guide?.summary ?? 'This guide could not be found.',
    noindex: guide === undefined,
  });

  if (guide === undefined) {
    return <NotFoundPage />;
  }

  const position = GUIDES.findIndex((candidate) => candidate.slug === guide.slug);
  const previous = position > 0 ? GUIDES[position - 1] : undefined;
  const next = position < GUIDES.length - 1 ? GUIDES[position + 1] : undefined;

  return (
    <div className="bg-background min-h-screen">
      <SiteHeader />

      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-10 lg:grid-cols-[248px_minmax(0,1fr)] lg:gap-12 xl:grid-cols-[248px_minmax(0,1fr)_200px]">
        {/* ── Every guide, always visible ─────────────────────────────────────────────────── */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <Link
              to="/docs"
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm transition-colors"
            >
              <BookOpen className="size-4" aria-hidden />
              Documentation
            </Link>

            <p className="text-muted-foreground mt-6 px-3 pb-2 text-[11px] font-medium tracking-wider uppercase">
              Guides
            </p>
            <nav>
              <ul className="space-y-1">
                {GUIDES.map((candidate) => (
                  <li key={candidate.slug}>
                    <NavLink
                      to={`/docs/${candidate.slug}`}
                      className={({ isActive }) =>
                        cn(
                          'flex items-start gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground font-medium'
                            : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                        )
                      }
                    >
                      <candidate.icon className="mt-0.5 size-4 shrink-0" aria-hidden />
                      <span>{candidate.title}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </aside>

        {/* ── The guide ───────────────────────────────────────────────────────────────────── */}
        <main className="min-w-0">
          <nav aria-label="Breadcrumb" className="text-muted-foreground text-sm">
            <ol className="flex flex-wrap items-center gap-2">
              <li>
                <Link to="/docs" className="hover:text-foreground transition-colors">
                  Documentation
                </Link>
              </li>
              <li aria-hidden>/</li>
              <li className="text-foreground">{guide.title}</li>
            </ol>
          </nav>

          <header className="mt-6">
            <div className="flex items-start gap-4">
              <span className="bg-primary/10 text-primary flex size-11 shrink-0 items-center justify-center rounded-xl">
                <guide.icon className="size-5" aria-hidden />
              </span>
              <div className="min-w-0">
                <h1 className="text-3xl font-semibold tracking-tight text-balance sm:text-4xl">
                  {guide.title}
                </h1>
                <p className="text-muted-foreground mt-3 text-lg leading-relaxed">
                  {guide.summary}
                </p>
              </div>
            </div>

            {/* Before the first step, because discovering a prerequisite at step four means
                starting again. */}
            <div className="border-primary/20 bg-primary/5 mt-8 rounded-lg border p-4">
              <p className="text-sm leading-relaxed">
                <span className="font-medium">Before you start: </span>
                <span className="text-muted-foreground">{guide.before}</span>
              </p>
            </div>
          </header>

          <GuideSteps guide={guide} />

          <nav
            aria-label="Guides"
            className="border-border/60 mt-16 grid gap-4 border-t pt-8 sm:grid-cols-2"
          >
            {previous === undefined ? (
              <span />
            ) : (
              <Link
                to={`/docs/${previous.slug}`}
                className="border-border/60 hover:border-primary/40 hover:bg-muted/30 group rounded-xl border p-4 transition-colors"
              >
                <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                  <ArrowLeft className="size-3.5" aria-hidden />
                  Previous
                </span>
                <span className="mt-1.5 block font-medium">{previous.title}</span>
              </Link>
            )}

            {next !== undefined && (
              <Link
                to={`/docs/${next.slug}`}
                className="border-border/60 hover:border-primary/40 hover:bg-muted/30 group rounded-xl border p-4 text-right transition-colors sm:col-start-2"
              >
                <span className="text-muted-foreground flex items-center justify-end gap-1.5 text-xs">
                  Next
                  <ArrowRight className="size-3.5" aria-hidden />
                </span>
                <span className="mt-1.5 block font-medium">{next.title}</span>
              </Link>
            )}
          </nav>
        </main>

        {/* ── This guide's steps ──────────────────────────────────────────────────────────── */}
        <aside className="hidden xl:block">
          <div className="sticky top-24">
            <p className="text-muted-foreground pb-3 text-[11px] font-medium tracking-wider uppercase">
              On this page
            </p>
            <ul className="border-border/60 space-y-2.5 border-l">
              {guide.steps.map((step, index) => (
                <li key={step.title}>
                  <a
                    href={`#${stepAnchor(step.title)}`}
                    className="text-muted-foreground hover:text-foreground -ml-px block border-l border-transparent pl-4 text-sm leading-snug transition-colors hover:border-current"
                  >
                    <span className="text-muted-foreground/60 mr-1.5 text-xs">{index + 1}.</span>
                    {step.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      <SiteFooter />
    </div>
  );
}
